import { InternationalMarket } from '@chewy/market-handler';
import Script from 'next/script';
import * as storefrontAnalyticsServerSide from 'storefront-analytics/dist/server-side';

import { ClientAnalytics } from '@/analytics/client-analytics';
import { UserContextData } from '@/providers/types';
import { getMarketConfig } from '@/utils/marketConfig';
import { LoyaltyPageData } from '@/utils/ssr';

interface ServerAnalyticsProps {
  market: string;
  userContextData: UserContextData;
  environment: string;
  pageData?: LoyaltyPageData;
  telemedicineEligible?: boolean;
}

export const SGEMENT_ID_PRD = 'uComl39TRW5htS34AmOAxlHsD30SduUc';
export const SGEMENT_ID_NON_PRD = 'bkPqspTFSa8BuYqZA9936VjCe2bYJh2r';

const SEGMENT_IDS: Record<string, Record<string, string>> = {
  [InternationalMarket.enUS]: {
    prd: SGEMENT_ID_PRD,
    nonprd: SGEMENT_ID_NON_PRD,
  },
  [InternationalMarket.enCA]: {
    prd: process.env.NEXT_PUBLIC_SEGMENT_ENV || 'k3HAVVVidjzpLlDLqEqwjt1dDDFqK3W4',
    nonprd: process.env.NEXT_PUBLIC_SEGMENT_ENV || 'i0IQ1D3ruojGcWwgfVNNjMe5Xk06Ig3P',
  },
};

export function handleSegmentId(market: string, environment: string): string {
  const envKey = environment?.toLowerCase() === 'prd' ? 'prd' : 'nonprd';
  return SEGMENT_IDS[market]?.[envKey] || SEGMENT_IDS[InternationalMarket.enUS][envKey];
}

type GetSnippets = (config: {
  gtmContainerId: string;
  gtmExtraParams: string;
  segmentEnvId: string;
}) => string;

// Package ships `export = any`; annotate the binding instead of asserting the module shape.
const getSnippets: GetSnippets = storefrontAnalyticsServerSide.getSnippets;

const getScriptContentsFromSnippet = (snippet: string) =>
  snippet
    .split('</script>')
    .map(script => script.split('<script>')[1])
    .filter((script): script is string => Boolean(script));

export function ServerAnalytics({
  market,
  userContextData,
  environment,
  pageData,
  telemedicineEligible,
}: ServerAnalyticsProps) {
  const analyticsToggles = userContextData?.analyticsToggles || {};
  const experimentOverrides = userContextData?.experimentOverrides || {};
  const segmentEnvId = handleSegmentId(market, environment);
  const marketConfig = getMarketConfig(market);

  const analyticsHeadScript = getSnippets({
    gtmContainerId: marketConfig.gtmId,
    gtmExtraParams: process.env.NEXT_PUBLIC_ANALYTICS_GTM_PARAMS ?? '',
    segmentEnvId,
  });

  const scriptElements = getScriptContentsFromSnippet(analyticsHeadScript).map((script, idx) => (
    <Script // https://nextjs.org/docs/pages/api-reference/components/head#use-nextscript-for-scripts
      dangerouslySetInnerHTML={{ __html: script }}
      id={`loyalty-analytics-header-script-${idx}`}
      key={`loyalty-analytics-header-script-${idx}`}
    />
  ));

  return [
    ...scriptElements,
    <ClientAnalytics
      analyticsToggles={analyticsToggles}
      experimentOverrides={experimentOverrides}
      key="loyalty-client-analytics"
      market={market}
      pageData={pageData}
      telemedicineEligible={telemedicineEligible}
      userContextData={userContextData}
    />,
  ];
}
